<template>
  <div
    class="sn-select"
    :style="containerWidth"
  >
    <input
      class="input-focus"
      type="text"
      @focus="focusSearch"
    >

    <sn-select-display
      v-model="options"
      :disabled="disabled"
      :settings="displaySettings"
      :tab-index="1"
      @toggle="toggleSelection"
      @optionSelected="optionSelected"
    />

    <div
      class="backdrop"
      v-if="isSelectionEnabled"
      @click="toggleSelection"
    >
    </div>

    <div
      class="input-block"
      v-if="isSelectionEnabled"
      :style="innerWidth"
      @click.stop
    >
      <div
        class="search-block"
        :class="{
          'hidden': !searchEnabled,
          'bordered': !hasOptions
        }"
        :width="selectionWidth + 'px'"
      >
        <p4a-icon
          class="search-icon"
          :icon="'search'"
          :color="'#58586a'"
        />

        <input
          type="text"
          ref="search-input"
          class="large no-borders"
          v-model="filterQuery"
          :placeholder="$t('multiselect.search')"
          v-focus
          @input="focusOption(0)"
          @keydown.tab="blurSearch"
          @keyup.enter.prevent="selectFocus"
          @keyup.esc.prevent="toggleSelection"
          @keydown.up.prevent="focusPrevOption"
          @keydown.down.prevent="focusNextOption"
        >

        <button
          class="empty-button"
          @click.stop="filterQuery = ''"
        >
          <p4a-icon
            class="svg-icon"
            :icon="'empty-text'"
            :color="'#58586a'"
          />
        </button>
      </div>

      <div
        class="multiselect-options-panel"
        v-if="isSelectionEnabled"
      >
        <sn-select-content
          ref="option-list"
          :options="optionList"
          :display-type="displayType"
          :settings="optionSettings"
          :container-width="optionStyling"
          @optionSelected="optionSelected"
          @clearOption="clearSelection"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import _ from 'lodash'
  import Vue from 'vue'

  import Locales from '../../../../localization/Locales'

  import SNSelectDisplay from './SNSelectDisplay'
  import SNSelectContent from './SNSelectContent'

  export default {
    name: 'SNSelect',
    components: {
      'sn-select-display': SNSelectDisplay,
      'sn-select-content': SNSelectContent
    },

    i18n: {
      messages: Locales
    },

    data () {
      return {
        isSelectionEnabled: false,
        filterQuery: '',
        selectedOptions: {},
        focusIndex: 0
      }
    },
    props: {
      disabled: {
        default: false,
        type: Boolean
      },
      displayType: {
        default: 'default',
        type: String
      },
      options: {
        default: function () {
          return []
        },
        type: Array
      },
      activeOptions: {
        default: function () {
          return []
        },
        type: Array
      },
      label: {
        default: '',
        type: String
      },
      subtitleKey: {
        default: '',
        type: String
      },
      contentHeight: {
        default: 150,
        type: Number
      },
      contentWidth: {
        default: null,
        type: Number
      },
      selectionWidth: {
        default: 275,
        type: Number
      },
      searchEnabled: {
        default: true,
        type: Boolean
      },
      settings: {
        default: function () {
          return {}
        },
        type: Object
      }
    },
    mounted () {
      let self = this

      _.forEach(self.activeOptions, optionData => {
        let key = optionData[self.identifier]
        self.selectedOptions[key] = _.cloneDeep(optionData)
      })
    },
    methods: {
      toggleSelection () {
        let self = this
        if (!self.disabled) {
          self.isSelectionEnabled = !self.isSelectionEnabled
          self.focusOption(0)

          if (!self.isValidOption(self.focusIndex)) {
            self.focusNextOption()
          }
        }
      },
      focusSearch () {
        let self = this
        if (!self.disabled) {
          self.toggleSelection()
        }
      },
      blurSearch (event) {
        let self = this
        self.toggleSelection()
        event.target.nextElementSibling.focus()
      },
      clearSelection () {
        let self = this
        self.selectedOptions = {}
        self.isSelectionEnabled = false

        self.broadcastSelectedOptionsChange()
      },
      optionSelected (filteredOption) {
        let self = this
        let searchCriteria = {}
        searchCriteria[self.identifier] = filteredOption[self.identifier]

        let foundOption = _.find(self.options, searchCriteria)
        foundOption.selected = !foundOption.selected
        filteredOption.selected = foundOption.selected

        if (self.singleSelect) {
          self.selectedOptions = {}
          self.isSelectionEnabled = false

          Vue.set(self.selectedOptions, foundOption[self.identifier], foundOption)
        } else if (foundOption.selected) {
          Vue.set(self.selectedOptions, foundOption[self.identifier], foundOption)
        } else {
          _.forEach(self.selectedOptions, (option, index) => {
            if (_.isEqual(foundOption[self.identifier], option[self.identifier])) {
              Vue.delete(self.selectedOptions, index)
            }
          })
        }

        self.broadcastSelectedOptionsChange()
      },
      broadcastSelectedOptionsChange () {
        this.$emit('selectionChanged', this.selectedOptions)
      },
      selectionText (option) {
        let self = this
        let result = option[self.label]
        if (!self.tall && !self.singleSelect) {
          result += ','
        }
        return result
      },
      selectionColor (option) {
        let result = null
        if (option.Color) {
          result = {
            'background-color': '#' + option.Color
          }
        }
        return result
      },
      selectFocus () {
        let self = this
        if (self.isValidOption(self.focusIndex)) {
          self.optionSelected(self.optionList[self.focusIndex])
        }
      },
      focusOption (targetIndex, focusIndex) {
        let self = this

        // only focus if the element actually exists
        if (self.$refs['option-list']) {
          let scrollElement = self.$refs['option-list'].$el

          // while scrolling display the previous option too
          let scrollMultiplier = Math.max(focusIndex, 0)

          scrollElement.scrollTop = ((self.selectionHeight - 2) * scrollMultiplier)
        }

        self.setFocus(targetIndex)
      },
      setFocus (focusIndex) {
        let self = this
        Vue.set(self, 'focusIndex', focusIndex)
      },
      focusPrevOption () {
        let self = this
        let newIndex = Math.max(self.focusIndex - 1, 0)

        // Loop through options until we find the next valid option
        while (!self.isValidOption(newIndex) && newIndex > 0) {
          newIndex--
        }

        // Ensure new option is valid (edge case check)
        if (self.isValidOption(newIndex)) {
          self.focusOption(newIndex, self.focusIndex - 2 - self.getHiddenItemsBeforeIndex(self.focusIndex))
        }
      },
      focusNextOption () {
        let self = this
        let newIndex = Math.min(self.focusIndex + 1, _.size(self.optionList) - 1)

        // Loop through options until we find the next valid option
        while (!self.isValidOption(newIndex) && _.size(self.optionList) - 1 > newIndex) {
          newIndex++
        }

        // Ensure new option is valid (edge case check)
        if (self.isValidOption(newIndex)) {
          self.focusOption(newIndex, self.focusIndex - self.getHiddenItemsBeforeIndex(self.focusIndex))
        }
      },
      isValidOption (index) {
        let self = this
        return self.optionList[index] &&
        !self.optionList[index].hidden &&
        !self.optionList[index].disabled
      },
      getHiddenItemsBeforeIndex (index) {
        let self = this
        return _.size(_.filter(_.take(self.optionList, index), 'hidden'))
      }
    },
    directives: {
      focus: {
        inserted: function (el) {
          el.focus()
        }
      }
    },
    computed: {
      index () {
        return this.settings.index ? this.settings.index : this.settings.identifier
      },
      identifier () {
        return this.settings.identifier
      },
      displayKey () {
        return this.settings.displayKey
      },
      singleSelect () {
        return this.settings.singleSelect
      },
      tall () {
        return this.settings.tall
      },
      selectionHeight () {
        return this.tall ? 52 : 27
      },
      containerWidth () {
        return {
          'cursor': this.disabled ? 'not-allowed' : 'pointer',
          'color': this.disabled ? 'lightGray' : '',
          'width': this.selectionWidth + 'px',
          'height': this.selectionHeight + 'px',
          'lineHeight': this.selectionHeight - 2 + 'px'
        }
      },
      optionStyling () {
        return {
          'cursor': this.disabled ? 'not-allowed' : 'pointer',
          'color': this.disabled ? 'lightGray' : '',
          'lineHeight': this.selectionHeight - 5 + 'px',
          'max-height': this.contentHeight + 'px'
        }
      },
      innerWidth () {
        let self = this
        if (self.contentWidth) {
          return {
            'width': self.contentWidth + 'px'
          }
        } else {
          return {
            'width': self.selectionWidth - 2 + 'px'
          }
        }
      },
      computedFilterQuery () {
        return this.filterQuery
      },
      optionList () {
        let self = this
        let result = (self.searchEnabled && self.filterQuery) ? self.filteredOptions : self.options

        result = _.map(result, option => {
          if (option.hidden === undefined) {
            option.hidden = false
          }
          return option
        })

        result = _.map(result, (option, index) => {
          let optionData = _.cloneDeep(option)
          optionData.isFocusedOption = _.isEqual(index, self.focusIndex)

          return optionData
        })

        return result
      },
      filteredOptions () {
        let self = this
        let filteredOptions = {}
        let lowerFilterQuery = self.filterQuery.toLowerCase()

        _.forEach(self.options, option => {
          option = _.cloneDeep(option)
          if (self.searchEnabled && lowerFilterQuery) {
            let loweredOptionLabel = _.clone(option[self.displayKey].toLowerCase())

            if (_.includes(loweredOptionLabel, lowerFilterQuery)) {
              Vue.set(filteredOptions, option[self.identifier], option)
            }
          } else {
            Vue.set(filteredOptions, option[self.identifier], option)
          }
        })
        return _.orderBy(filteredOptions, self.index)
      },
      hasOptions () {
        let self = this
        return _.size(self.filteredOptions) > 0
      },
      displaySettings () {
        let self = this
        let result = {
          displayType: self.displayType,
          subtitleKey: self.subtitleKey,
          displayKey: self.label,
          showColor: self.displayColor,
          colorKey: 'Color',
          tall: self.tall,
          singleSelect: self.singleSelect,
          leftIcon: self.leftIcon
        }

        if (_.size(self.settings) > 0) {
          result = self.settings
        }

        return result
      },
      optionSettings () {
        let self = this
        let result = {
          subtitleKey: self.subtitleKey,
          displayKey: self.label,
          showColor: self.displayColor,
          colorKey: 'Color',
          tall: self.tall,
          singleSelect: self.singleSelect
        }

        result = self.settings

        return result
      }
    },
    watch: {
      activeOptions: {
        handler: function (newValue) {
          let self = this
          if (!_.isEqual(newValue, self.selectedOptions)) {
            let newSelection = {}
            _.forEach(self.activeOptions, optionData => {
              let key = optionData[self.identifier]
              newSelection[key] = _.cloneDeep(optionData)
            })
            self.selectedOptions = newSelection
          }
        },
        deep: true,
        immediate: true
      },
      selectedOptions: {
        handler: function () {
        },
        deep: true,
        immediate: true
      },
      options: {
        handler: function () {
        },
        deep: true,
        immediate: true
      },
      isSelectionEnabled: {
        handler: function (newValue) {
          let self = this
          self.$emit('isOpen', newValue)
        },
        deep: true,
        immediate: true
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '../../uikit';

  input {
    font-size: 14px;
  }

  .sn-select {
    font-size: 0;
    position: relative;
    border: $primaryBorderColor solid 1px;

    .input-focus {
      height: 0;
      width: 0;
      border: none;
      outline: none;
      padding: 0;
      display: inline-block;
      font-size: 0;
      line-height: 0;
    }

    .sn-select-display {
      display: inline-block;
      width: 100%;
    }

    div {
      background: white;

      &.input-block {
        position: relative;
        background-color: white;
        box-sizing: border-box;
        border-radius: 2px;
        overflow: hidden;
        z-index: 29;
        font-size: 0;
        top: 6px;

        -webkit-box-shadow: 0 0 5px 0 rgb(175, 175, 175);
        -moz-box-shadow: 0 0 5px 0 rgb(175, 175, 175);
        box-shadow: 0 0 5px 0 rgb(175, 175, 175);

        input {
          width: calc(100% - 100px);
          height: calc(100% - 2px);
          vertical-align: top;
          text-indent: 0;

          &[type="checkbox"] {
            width: 16px;
          }
        }

        .search-block {
          border-bottom: 1px solid $primaryBorderColor;
          height: 50px;
          width: 100%;

          input[type="text"] {
            display: inline-block;
            height: 100%;
            vertical-align: top;
            width: calc(100% - 100px);
          }

          .search-icon {
            display: inline-block;
            height: 100%;
            padding: 16px;
            width: 50px;
          }

          .empty-button {
            display: inline-block;
            background-color: transparent;
            margin: 0;
            padding: 0;
            height: 100%;
            vertical-align: top;
            width: 50px;

            .svg-icon {
              height: 50px;
              width: 50px;
              padding: 18px;
            }
          }

          &.hidden {
            height: 0;
            overflow: hidden;
          }
        }

        button {
          width: 50px;
          height: calc(100% - 2px);
          margin: 0;
          line-height: 100%;
        }

      }

      &.backdrop {
        position: fixed;
        z-index: 28;
        background: none;
        bottom: 0;
        top: 0;
        left: 0;
        right: 0;
        width: 100%;
        height: 100%;
      }
    }
  }

</style>



// WEBPACK FOOTER //
// node_modules/pro4all-ui/sns-components/kit-components/input-templates/selection/SNSelect.vue